export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: Date;
  isAdmin?: boolean;
  /** An unspent conference sign-up link, as held in user_metadata. */
  signupToken?: string;
}

export interface Message {
  id: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  leads?: Lead[];
}

export interface Lead {
  id: string;
  type: 'company' | 'person';
  name: string;
  data: Record<string, any>;        // Clean data - no metadata suffixes
  cellStates?: Record<string, import('./table').CellState>; // Separate cell states (loading, editing)
  metadata?: import('./table').RowMetadata;  // Separate row metadata (displayUrl, etc.)
  reasoning?: Record<string, any>;
  createdAt: Date;
  updatedAt?: Date;
  sourceType?: 'search' | 'import' | 'monitor';  // Origin of the lead (search, CSV import, or monitor)
  sourceId?: string;  // Reference ID for the source (e.g., mrun_xxx for monitor runs)
}

export interface Conversation {
  id: string;
  messages: Message[];
  leads: Lead[];
  createdAt: Date;
  updatedAt: Date;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface OnboardingData {
  company?: {
    description?: string | null;
    value_proposition?: string | null;
    customer_examples?: Array<{ name: string; website?: string; industry?: string }>;
  };
  preferences?: {
    autonomy_level?: string | null;
  };
  portal_welcome_done_at?: string | null;
}

export interface UserProfile {
  id: string;
  user_id: string;
  org_id?: string | null;
  /** Embedded org flag — client workspaces route to /portal only */
  organizations?: { is_client_workspace: boolean } | null;
  company_name?: string | null;
  company_website: string | null;
  onboarding_completed: boolean;
  role?: string | null;
  primary_goal?: string | null;
  referral_source?: string | null;
  onboarding_data?: OnboardingData | null;
  created_at: string;
  updated_at: string;
}

export interface IdealCustomerProfile {
  id: string;
  user_id: string;
  name: string;
  solution_products: string;
  target_region: string[];
  target_customers: string;
  company_sizes: string[];
  funding_stages: string[];
  locations: string[];
  titles: string;
  // ICP Segmentation fields
  customer_type?: string;  // B2B, B2C, or Both
  selling_motion?: string;  // sales-led, plg, or services
  target_verticals?: string[];  // Target industry verticals
  created_at: string;
  updated_at: string;
}

// Lead Table Status Constants
export const LEAD_TABLE_STATUS = {
  GENERATING: 'generating',
  COMPLETED: 'completed',
  COMPLETED_NO_RESULTS: 'completed_no_results',
  ERROR_TIMEOUT: 'error_timeout',
  ERROR_CONFIG: 'error_config',
  ERROR: 'error',
  ERROR_PROVIDER_API: 'error_provider_api',
  ERROR_RATE_LIMIT: 'error_rate_limit',
  ERROR_INVALID_QUERY: 'error_invalid_query',
  ERROR_SERVER: 'error_server',
} as const;

export type LeadTableStatus = typeof LEAD_TABLE_STATUS[keyof typeof LEAD_TABLE_STATUS];

// Signal Status Constants
export const SIGNAL_STATUS = {
  DEPLOYED: 'deployed',
  SEARCHING: 'searching',
  COMPLETED: 'completed',
} as const;

export type SignalStatus = typeof SIGNAL_STATUS[keyof typeof SIGNAL_STATUS];

// AI-generated signal suggestion with full details (not stored in DB)
export interface SignalSuggestion {
  id?: string;
  name: string;
  description: string;
  search_criteria: string[];
  target_entity: 'companies' | 'people' | 'custom';
  rationale: string;
  priority: string;
}

// Actual stored signal in database
export interface LeadSignal {
  id: string;
  user_id: string;
  icp_id: string;
  name: string;
  description?: string; // Keep for backward compatibility
  status: SignalStatus;
  criteria?: {
    signal_criteria?: string[]; // Up to 5 search criteria
    target_entity?: 'companies' | 'people' | 'custom';
    entity_description?: string; // Required when target_entity is 'custom'
    source?: string; // 'ai_generated' | 'manual_input'
  };
  created_at: string;
  updated_at: string;
}

export interface LeadTable {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  table_type: 'companies' | 'people' | 'custom';
  default_columns: any[];
  status?: LeadTableStatus;
  error_message?: string;
  signal_id?: string | null;  // Links table to the signal that created it
  icp_id?: string | null;     // Direct ICP association
  // Note: suggested_action is derived from status in the API layer, not stored in DB
  created_at: string;
  updated_at: string;
  lead_count?: number;
}

// Error display information for deterministic error handling
export interface ErrorDisplayInfo {
  title: string;
  message: string;
  icon: string; // lucide-react icon name
  color: string; // Tailwind color class
  bgColor: string; // Tailwind background color class
  suggestedAction: string;
}

export interface BulkEmailData {
  leadData: Lead;
  recipientEmail: string;
}

// User Action Logging Types
export interface UserActionRequest {
  action_type: string;
  item_type?: string;
  item_id?: string;
  session_id?: string;
  request_data?: Record<string, any>;
  metadata?: Record<string, any>;
  referrer?: string;
  response_time?: number;
  success?: boolean;
  error_message?: string;
}

export interface UserActionResponse {
  success: boolean;
  action_id?: string;
  message: string;
}

export interface UserAction {
  id: string;
  user_id?: string;
  session_id?: string;
  action_type: string;
  item_type?: string;
  item_id?: string;
  request_url?: string;
  referrer?: string;
  user_agent?: string;
  response_time?: number;
  success: boolean;
  error_message?: string;
  request_data?: Record<string, any>;
  metadata?: Record<string, any>;
  created_at: string;
  timestamp: string;
}

// Analytics helper types
export type ActionType = 'click' | 'submit' | 'view' | 'download' | 'search' | 'navigate' | 'error' | 'performance';
export type ItemType = 'button' | 'form' | 'page' | 'file' | 'link' | 'modal' | 'dropdown' | 'table';

// Credits & Billing types
export interface TopUpPlan {
  id: string;
  name: string;
  pricing: number;
  credits: number;
  is_popular: boolean;
}

export interface LeadGenProgress {
  found: number;
  analyzed: number;
  completion: number;
  timeLeft: number;
  status: string;
}

export interface AutoFindMoreStatus {
  enabled: boolean;
  maxAttempts: number;
  currentAttempt: number;
  status: 'idle' | 'in_progress' | 'exhausted' | 'completed' | 'max_reached' | 'limit_reached' | 'error';
  disabledReason?: 'manual_override';
  lastTriggeredAt?: string;
  history?: Array<{
    attempt: number;
    searchId?: string;
    requested: number;
    found?: number;
    triggeredAt?: string;
    completedAt?: string;
  }>;
  error?: string;
}

export interface EnrichmentProgress {
  column: string;
  total: number;
  progress: number; // for showing a random progress (0-100) for UI visualization
  status: string;
}

export interface Dataset {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  table_type?: string;
  lead_count?: number;
  status?: string;
  error_message?: string;
  user_prompt?: string;
  icp_id?: string | null;
}

// Notification Preferences Types
export interface NotificationPreference {
  category: string;
  label: string;
  description: string;
  enabled: boolean;
}

export interface NotificationPreferencesResponse {
  categories: NotificationPreference[];
}

export interface NotificationPreferenceUpdate {
  category: string;
  enabled: boolean;
}

// Notification category definitions for UI
export const NOTIFICATION_CATEGORIES = [
  {
    category: 'product_updates',
    label: 'Product Updates',
    description: 'Notifications about your datasets, monitors, and workflow results',
  },
  {
    category: 'marketing',
    label: 'Marketing & Announcements',
    description: 'Product tips, feature announcements, and newsletters',
  },
] as const;

// LinkedIn Types
export interface LinkedInConnection {
  id: string;
  linkedin_email: string;
  display_name: string | null;
  is_primary: boolean;
  is_active: boolean;
  token_status: string;
  expires_at: string | null;
  last_used_at: string | null;
  error_count: number;
  created_at: string | null;
}

export interface LinkedInConnectionStatus {
  is_connected: boolean;
  linkedin_email: string | null;
  display_name: string | null;
  expires_at: string | null;
  token_status: string | null;
  credential_id: string | null;
  last_used_at: string | null;
  error_count: number;
}